import React from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { FileDown } from 'lucide-react';
import PackagePDF from './PackagePDF';
import { getImageUrl } from '../../utils/imageUrl';
import type { Package, TravelAgency, Theme } from '../../types/pdf';

interface DownloadPackageButtonProps {
  package: Package;
  agency: TravelAgency;
  className?: string;
  theme?: Theme;
}

const DownloadPackageButton: React.FC<DownloadPackageButtonProps> = ({ 
  package: pkg, 
  agency, 
  className = '',
  theme = {
    primaryColor: '#3B82F6',
    secondaryColor: '#1E40AF',
    backgroundColor: '#F3F4F6'
  }
}) => {
  const hasCustomPdf = !!(pkg as Package & { programPdf?: string }).programPdf;

  if (hasCustomPdf) {
    const pdfUrl = getImageUrl((pkg as Package & { programPdf: string }).programPdf);
    return (
      <a
        href={pdfUrl}
        download={`${pkg.title}-${agency.name}.pdf`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ 
          backgroundColor: theme.primaryColor,
          color: '#FFFFFF'
        }}
        className={`
          w-full flex items-center justify-center gap-2 
          px-4 py-3 rounded-lg 
          hover:opacity-90 transition-all duration-200
          ${className}
        `}
      >
        <FileDown className="w-5 h-5" />
        <span>Télécharger le PDF</span>
      </a>
    );
  }

  return (
    <PDFDownloadLink
      document={<PackagePDF package={pkg} agency={agency} />}
      fileName={`${pkg.title}-${agency.name}.pdf`}
    >
      {({ loading, error }) => (
        <button
          style={{ 
            backgroundColor: theme.primaryColor,
            color: '#FFFFFF'
          }}
          className={`
            w-full flex items-center justify-center gap-2 
            px-4 py-3 rounded-lg 
            hover:opacity-90 transition-all duration-200
            disabled:opacity-50 disabled:cursor-not-allowed
            ${className}
          `}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
              <span>Génération du PDF...</span>
            </>
          ) : error ? (
            <>
              <span className="text-red-500">Erreur</span>
              <span>Réessayer</span>
            </>
          ) : (
            <>
              <FileDown className="w-5 h-5" />
              <span>Télécharger le PDF</span>
            </>
          )}
        </button>
      )}
    </PDFDownloadLink>
  );
};

export default DownloadPackageButton; 